import React, { useEffect, useState } from 'react';
import Modal from '../ui/Modal';
import Input from '../ui/Input';
import Button from '../ui/Button';
import { ISenderAccount } from 'types';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import {
  clearSenderAccountState,
  selectSenderAccountSuccess,
  selectSenderAccountError,
} from 'store/senderAccounts';
import { useToast } from 'contexts/ToastContext';
import { useDispatch } from 'react-redux';

interface FormData {
  displayName: string;
  emailAddress: string;
  providerType: 'aws' | 'mailgun';
  _id?: string;
}

interface AddSenderAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: FormData) => void;
  domain?: string;
  senderAccount?: ISenderAccount;
  providerType: 'aws' | 'mailgun';
}

const AddSenderAccountModal: React.FC<AddSenderAccountModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  domain,
  senderAccount,
  providerType,
}) => {
  const [formData, setFormData] = useState<FormData>({
    displayName: '',
    emailAddress: '',
    providerType: providerType,
  });
  const [errors, setErrors] = useState<Partial<FormData>>({});
  const isloading = useSelector(
    (state: RootState) => state.senderAccounts.loading
  );
  const success = useSelector(selectSenderAccountSuccess) as boolean;
  const error = useSelector(selectSenderAccountError);
  const { showToast } = useToast();
  const dispatch = useDispatch();
  useEffect(() => {
    if (senderAccount) {
      const data = {
        displayName: senderAccount.name,
        emailAddress: senderAccount.email.replace(`@${domain}`, ''),
        providerType: senderAccount?.providerType ?? providerType,
      };
      setFormData({ ...data });
    } else {
      setFormData({
        displayName: '',
        emailAddress: '',
        providerType: providerType,
      });
    }
  }, [senderAccount, domain, providerType]);

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<FormData> = {};

    if (!formData.displayName.trim()) {
      newErrors.displayName = 'Sender Name is required';
    }

    if (!formData.emailAddress.trim()) {
      newErrors.emailAddress = 'Sender Address is required';
    } else if (!/^[^\s@]+$/.test(formData.emailAddress)) {
      newErrors.emailAddress =
        'Please enter a valid email address (without @ symbol)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    if (success) {
      let successMessage = senderAccount
        ? 'Sender Account updated successfully'
        : 'Sender Account added successfully';
      showToast({
        type: 'success',
        title: 'Success',
        message: successMessage,
      });
      setFormData({
        displayName: '',
        emailAddress: '',
        providerType: providerType,
      });
      setErrors({});
      dispatch(clearSenderAccountState());
      onClose();
    }
  }, [
    success,
    senderAccount,
    domain,
    providerType,
    showToast,
    dispatch,
    onClose,
  ]);

  useEffect(() => {
    if (error) {
      showToast({
        type: 'error',
        title: 'Error',
        message: error,
      });
      dispatch(clearSenderAccountState());
    }
  }, [error, showToast, dispatch]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Combine email address with domain
    const fullEmailAddress = `${formData.emailAddress}@${domain}`;
    if (senderAccount) {
      onSubmit({
        ...formData,
        emailAddress: fullEmailAddress,
        _id: senderAccount._id,
        providerType: senderAccount?.providerType ?? providerType,
      });
    } else {
      onSubmit({
        ...formData,
        emailAddress: fullEmailAddress,
        providerType: providerType,
      });
    }
  };

  const handleCancel = () => {
    // Reset form
    setFormData({
      displayName: '',
      emailAddress: '',
      providerType: providerType,
    });
    setErrors({});
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCancel}
      title={senderAccount ? 'Edit From Address' : 'Add From Address'}
      size="medium"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-4">
          <Input
            label="Sender Name"
            placeholder="Your Name"
            value={formData.displayName}
            onChange={(e) => handleInputChange('displayName', e.target.value)}
            required
          />
          {errors.displayName && (
            <p className="mt-1 text-sm text-destructive">
              {errors.displayName}
            </p>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Sender Address <span className="text-destructive">*</span>
            </label>
            <div className="flex">
              <input
                type="text"
                className={`flex-1 px-3 py-2 border ${errors.emailAddress ? 'border-red-300' : 'border-gray-300'} rounded-l-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-colors`}
                placeholder="hello"
                value={formData.emailAddress}
                onChange={(e) =>
                  handleInputChange('emailAddress', e.target.value)
                }
                required
              />
              <div className="px-3 py-2 bg-gray-50 border border-l-0 border-gray-300 rounded-r-lg text-gray-600 text-sm flex items-center">
                @{domain}
              </div>
            </div>
            {errors.emailAddress && (
              <p className="mt-1 text-sm text-destructive">
                {errors.emailAddress}
              </p>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3 pt-4">
          <Button type="button" variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={isloading}>
            {isloading ? 'Adding...' : 'Add From Address'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default AddSenderAccountModal;
